import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { search } from "../redux/nodesSlice";
import Utils from "../utils/Utils";
import Loader from "./Loader";

const minCharsToSearch = 2; // Reduced from 3 to make search more responsive
const searchDebounceMs = 300; // Debounce delay for API search
const { stringIncludesCaseInsensitive } = Utils;

function SearchBox({
  parent,
  updateParent,
  setAlertModalContent,
  setDeepParent,
  t,
}) {
  const { t: translate } = useTranslation();
  const translateText = t || translate;
  const { searchStatus, searchResult, nodesIndex } = useSelector(
    (state) => state.nodes
  );
  const dispatch = useDispatch();
  const [searchKey, setSearchKey] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState([]);
  const debounceTimerRef = useRef(null);

  // When search box is focused but empty, show current children
  useEffect(() => {
    if (isFocused && searchKey === "") {
      // Show all searchable children of current node
      if (parent?.children && parent.children.length > 0) {
        setResults(
          parent.children
            .filter((child) => child.searchable)
            .map((item) => ({ ...item, isLocal: true }))
        );
      } else {
        setResults([]);
      }
    }
  }, [isFocused, searchKey, parent]);

  // Handle search - local for short queries, API for longer
  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (searchKey.length === 0) {
      // Empty search handled by focus effect above
      return;
    }

    if (searchKey.length < minCharsToSearch) {
      // Very short search - only local filtering (no debounce needed)
      if (parent?.children) {
        setResults(
          parent.children
            .filter(
              (child) =>
                child.searchable &&
                stringIncludesCaseInsensitive(child.name, searchKey)
            )
            .map((item) => ({ ...item, isLocal: true }))
        );
      }
    } else {
      // Longer search - use API with debounce to avoid too many requests
      debounceTimerRef.current = setTimeout(() => {
        dispatch(search(searchKey));
      }, searchDebounceMs);
    }

    // Cleanup function to clear timer on unmount or when searchKey changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchKey, dispatch, parent]);

  // Update results when API search completes
  useEffect(() => {
    if (searchResult && searchResult.length > 0) {
      // Normalize search results (backend uses 'path', frontend uses 'numberedPath')
      const normalizedResults = searchResult.map((item) => {
        const normalized = { ...item };
        if (item.path && !item.numberedPath) {
          normalized.numberedPath = item.path;
        }
        // Check if this node is in current tree (local)
        normalized.isLocal = !!(
          nodesIndex && nodesIndex[normalized.numberedPath]
        );
        return normalized;
      });
      setResults(normalizedResults);
    } else if (
      searchKey.length >= minCharsToSearch &&
      searchStatus === "success"
    ) {
      // API search completed but no results
      setResults([]);
    }
  }, [searchResult, searchStatus, nodesIndex, searchKey]);

  return (
    <div className="search-box">
      <TextField
        id="search"
        label={translateText("searchInterest")}
        variant="outlined"
        sx={{ width: "100%" }}
        value={searchKey}
        onChange={(e) => setSearchKey(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)} // Delay to allow click
        placeholder={
          isFocused && searchKey === ""
            ? translateText("clickToSeeCurrentChildren")
            : ""
        }
      />
      <Box className={`wrapper ${isFocused && "expanded"}`}>
        <Box className="results">
          {searchStatus === "loading" ? (
            <Loader />
          ) : results.length === 0 && isFocused ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {searchKey
                  ? translateText("noResultsFound")
                  : translateText("noChildrenToShow")}
              </Typography>
            </Box>
          ) : (
            results.map((item) => (
              <Button
                key={item.numberedPath || item._id || item.name}
                variant="outlined"
                color="inherit"
                sx={{ margin: 0.5, borderRadius: 5 }}
                onMouseDown={() => {
                  setDeepParent({
                    pathToParent: item.path,
                    numberedPath: item.numberedPath,
                    isLocal: item.isLocal,
                    newParentID: item._id,
                  });
                  setSearchKey("");
                  setResults([]);
                }}
              >
                <Typography>{item.name}</Typography>
              </Button>
            ))
          )}
        </Box>
      </Box>
    </div>
  );
}

export default SearchBox;
